import type { Rsd } from '../core/money.ts';
import type { Lang } from '../i18n/strings.ts';
import { request, stream, WEB_BASE_URL, type StreamFrame } from './client.ts';
import type {
  AddressCreated,
  AvailabilityItem,
  CancelResult,
  CartQuote,
  Category,
  CollectionView,
  CreditsView,
  DeliveryQuote,
  DeliveryZone,
  HomeFeed,
  MyAddress,
  NewAddress,
  NotificationSettings,
  NotificationsPage,
  Order,
  OrderCreated,
  OrderItemInput,
  OrderListItem,
  OrderTracking,
  OtpRequested,
  PaymentConfig,
  PaymentConfirmation,
  PaymentResult,
  SavedCard,
  Profile,
  RatingResult,
  RecentOrder,
  RelatedProducts,
  SignedIn,
  StorefrontItem,
  StoreStatus,
  SupplementQuote,
  SupplementResult,
  SupportView, CardVerificationResult, CardVerificationStart } from './types.ts';

/** Все ручки API одним слоем: экраны и хранилища не собирают строки путей руками. */

/**
 * За какой склад отвечает витрина: сохранённый адрес вошедшего, точка на карте, выбранная
 * гостем, или ничего — тогда сервер отвечает от склада по умолчанию. Каждый запрос витрины
 * несёт это параметрами.
 */
export type StorefrontScope =
  | { kind: 'none' }
  | { kind: 'address'; addressId: string }
  | { kind: 'point'; lat: number; lng: number };

export const NO_SCOPE: StorefrontScope = { kind: 'none' };

export function scopeQuery(scope: StorefrontScope): Record<string, string | number> {
  if (scope.kind === 'address') return { addressId: scope.addressId };
  if (scope.kind === 'point') return { lat: scope.lat, lng: scope.lng };
  return {};
}

/** Устойчивый ключ области для кешей запросов: смена перечитывает экран. */
export function scopeKey(scope: StorefrontScope): string {
  if (scope.kind === 'address') return scope.addressId;
  if (scope.kind === 'point') return `${scope.lat},${scope.lng}`;
  return '-';
}

export function sameScope(a: StorefrontScope, b: StorefrontScope): boolean {
  return scopeKey(a) === scopeKey(b);
}

/** Источник списка товаров: категория, поиск, подборка, скидки, новинки, избранное, покупки. */
export type ListSource =
  | { kind: 'category'; id: string }
  | { kind: 'search'; text: string }
  | { kind: 'collection'; id: string }
  | { kind: 'deals' }
  | { kind: 'newItems' }
  | { kind: 'favorites' }
  | { kind: 'purchases' };

export const auth = {
  requestCode: (phone: string, bySms = false) =>
    request<OtpRequested>('/v1/auth/otp/request', { method: 'POST', body: bySms ? { phone, channel: 'sms' } : { phone } }),
  verifyCode: (phone: string, code: string) =>
    request<SignedIn>('/v1/auth/otp/verify', {
      method: 'POST',
      body: { phone, code, role: 'client', device: `Web ${navigator.platform}` },
    }),
  logout: () => request<{ ok: boolean }>('/v1/auth/logout', { method: 'POST' }),
};

export const me = {
  get: () => request<Profile>('/v1/me'),
  /** Сохранённые карты, карта по умолчанию первой; `sync` перечитывает кошелёк эквайера. */
  cards: (sync = false) => request<{ items: SavedCard[] }>(`/v1/me/cards${sync ? '?sync=1' : ''}`),
  setDefaultCard: (cardId: string) => request<{ ok: boolean }>(`/v1/me/cards/${cardId}/default`, { method: 'POST' }),
  deleteCard: (cardId: string) => request<{ ok: boolean }>(`/v1/me/cards/${cardId}`, { method: 'DELETE' }),
  verifyCard: (body: { returnTo: string; lang: string }) => request<CardVerificationStart>('/v1/me/cards/verify', { method: 'POST', body }),
  confirmCardVerification: (verificationId: string) => request<CardVerificationResult>(`/v1/me/cards/verify/${verificationId}/confirm`, { method: 'POST' }),
  /** Пустое имя, `avatarUrl: null` и `birthDate: null` убирают поле. */
  update: (patch: { name?: string; avatarUrl?: null; birthDate?: string | null; email?: string }) =>
    request<{ ok: boolean }>('/v1/me', { method: 'PATCH', body: patch }),
  uploadAvatar: (file: Blob) => {
    const form = new FormData();
    form.set('file', file, 'avatar.jpg');
    return request<{ avatarUrl: string }>('/v1/me/avatar', { method: 'POST', body: form });
  },
  remove: () => request<{ deleted: boolean }>('/v1/me', { method: 'DELETE' }),
  /** Снимок корзины для доски заказов: сервер её только смотрит, правда остаётся в браузере. */
  reportCart: (addressId: string | null, items: OrderItemInput[]) =>
    request<{ ok: boolean }>('/v1/me/cart', { method: 'PUT', body: { ...(addressId ? { addressId } : {}), items } }),
  credits: () => request<CreditsView>('/v1/me/credits'),
  recentOrders: (limit: number) => request<{ items: RecentOrder[] }>('/v1/me/orders/recent', { query: { limit } }),
};

export const addresses = {
  list: () => request<{ items: MyAddress[] }>('/v1/me/addresses'),
  create: (body: NewAddress) => request<AddressCreated>('/v1/me/addresses', { method: 'POST', body }),
  remove: (addressId: string) => request<{ removed: boolean }>(`/v1/me/addresses/${addressId}`, { method: 'DELETE' }),
  /** Адрес вне зон: сообщить о начале доставки, придёт уведомление `zone_open`. */
  requestCoverage: (addressId: string) =>
    request<{ ok: boolean }>('/v1/me/coverage-requests', { method: 'POST', body: { addressId } }),
};

export const delivery = {
  quote: (scope: StorefrontScope) => request<DeliveryQuote>('/v1/delivery/quote', { query: scopeQuery(scope) }),
  /** Условия для точки на карте: до сохранения адреса надо знать, доставляем ли сюда. */
  /** `text` — подпись пина: сервер хранит её со спросом вне зон и называет адрес в уведомлении `zone_open`. */
  quoteByPoint: (lat: number, lng: number, text?: string) =>
    request<DeliveryQuote>('/v1/delivery/quote', { query: text ? { lat, lng, text } : { lat, lng } }),
  zones: () => request<{ items: DeliveryZone[] }>('/v1/delivery/zones'),
};

export const store = {
  /** Статус склада адреса: без адреса — склада по умолчанию. */
  status: (scope: StorefrontScope) => request<StoreStatus>('/v1/store/status', { query: scopeQuery(scope) }),
};

export const catalog = {
  categories: (lang: Lang) => request<{ items: Category[] }>('/v1/catalog/categories', { query: { lang } }),
  home: (scope: StorefrontScope, lang: Lang) =>
    request<HomeFeed>('/v1/catalog/home', { query: { ...scopeQuery(scope), lang } }),
  /** Список товаров склада области по источнику; подборка приходит со своим заголовком. */
  list: async (
    source: ListSource,
    scope: StorefrontScope,
    lang: Lang,
    signal?: AbortSignal,
  ): Promise<{ items: StorefrontItem[]; collection: CollectionView | null }> => {
    const query: Record<string, string | number> = { ...scopeQuery(scope), lang, limit: 300 };
    const options = signal ? { query, signal } : { query };
    switch (source.kind) {
      case 'collection': {
        const collection = await request<CollectionView>(`/v1/catalog/collections/${source.id}`, options);
        return { items: collection.items, collection };
      }
      case 'favorites':
        return { ...(await request<{ items: StorefrontItem[] }>('/v1/me/favorites', options)), collection: null };
      case 'purchases':
        query.limit = 200;
        return { ...(await request<{ items: StorefrontItem[] }>('/v1/me/purchases', options)), collection: null };
      case 'category':
        query.categoryId = source.id;
        break;
      case 'search':
        query.search = source.text;
        break;
      case 'deals':
        query.onlyDeals = 'true';
        break;
      case 'newItems':
        query.onlyNew = 'true';
        break;
    }
    return { ...(await request<{ items: StorefrontItem[] }>('/v1/catalog', options)), collection: null };
  },
  /** Товары витрины по идентификаторам: товар, недоступный на складе области, не приходит. */
  byIds: async (ids: string[], scope: StorefrontScope, lang: Lang): Promise<StorefrontItem[]> => {
    if (ids.length === 0) return [];
    const response = await request<{ items: StorefrontItem[] }>('/v1/catalog', {
      query: { ...scopeQuery(scope), lang, ids: ids.join(',') },
    });
    return response.items;
  },
  /** Товары склада области в порядке каталога: главная без подборок и лент. */
  storefront: (scope: StorefrontScope, lang: Lang, limit: number) =>
    request<{ items: StorefrontItem[] }>('/v1/catalog', { query: { ...scopeQuery(scope), lang, limit } }),
  purchases: (scope: StorefrontScope, lang: Lang, limit: number) =>
    request<{ items: StorefrontItem[] }>('/v1/me/purchases', { query: { ...scopeQuery(scope), lang, limit } }),
  /** Наличие и цена названных товаров; сервер принимает до 200 штук за запрос. */
  availability: (scope: StorefrontScope, productIds: string[]) =>
    request<{ items: AvailabilityItem[] }>('/v1/catalog/availability', {
      method: 'POST',
      body: { ...scopeQuery(scope), productIds },
    }),
  /** Рекомендации к товару: «часто берут вместе» и «похожее». */
  related: (productId: string, scope: StorefrontScope, lang: Lang) =>
    request<RelatedProducts>('/v1/catalog/related', { query: { ...scopeQuery(scope), productId, lang } }),
  recommendations: (productIds: string[], scope: StorefrontScope, lang: Lang) =>
    request<{ items: StorefrontItem[] }>('/v1/catalog/recommendations', {
      query: { ...scopeQuery(scope), productIds: productIds.join(','), lang },
    }),
};

export const favorites = {
  ids: () => request<{ ids: string[] }>('/v1/me/favorites/ids'),
  add: (productId: string) => request<{ ok: boolean }>(`/v1/me/favorites/${productId}`, { method: 'PUT', body: {} }),
  remove: (productId: string) => request<{ removed: boolean }>(`/v1/me/favorites/${productId}`, { method: 'DELETE' }),
};

export const orders = {
  /** `status: 'open'` — только заказы в работе; без фильтра список целиком, новые сверху. */
  list: (params: { status?: 'open'; limit?: number } = {}) =>
    request<{ items: OrderListItem[]; total: number }>('/v1/orders', {
      query: { limit: params.limit ?? 50, status: params.status },
    }),
  get: (orderId: string) => request<Order>(`/v1/orders/${orderId}`),
  /**
   * Стоимость доставки в теле заказа не передаётся: сервер принимает её только от
   * сотрудника, а клиенту считает сам.
   */
  create: (body: {
    addressId: string;
    items: OrderItemInput[];
    comment?: string | undefined;
    leaveAtDoor: boolean;
    promoCode?: string | undefined;
    contactPhone?: string | undefined;
  }) => request<OrderCreated>('/v1/orders', { method: 'POST', body }),
  /** Скидки для корзины до оформления: те же правила, что применит заказ. */
  quote: (items: OrderItemInput[], promoCode: string | null, addressId: string | null) =>
    request<CartQuote>('/v1/orders/quote', {
      method: 'POST',
      body: { items, ...(promoCode ? { promoCode } : {}), ...(addressId ? { addressId } : {}) },
    }),
  /**
   * Оплата: без токена сервер открывает сессию на странице эквайера и отвечает `redirect` с адресом;
   * `returnTo` — куда страница подтверждения вернёт клиента. С токеном — списание по нему.
   */
  pay: (orderId: string, options: { cardToken?: string; savedCardId?: string; newCard?: boolean; returnTo?: string; lang?: Lang } = {}) =>
    request<PaymentResult>(`/v1/orders/${orderId}/pay`, { method: 'POST', body: options }),
  /** Результат оплаты на странице эквайера: сервер спрашивает эквайера и записывает ответ. */
  confirmPayment: (orderId: string) => request<PaymentConfirmation>(`/v1/orders/${orderId}/pay/confirm`, { method: 'POST' }),
  /** Добавить товары в оплаченный заказ, пока его не собрали: сколько доплатить и новый итог. */
  supplementQuote: (orderId: string, items: OrderItemInput[]) =>
    request<SupplementQuote>(`/v1/orders/${orderId}/supplement/quote`, { method: 'POST', body: { items } }),
  /** Добавить и доплатить отдельным списанием; отказ по карте оставляет заказ прежним. */
  /** Без `cardToken` доплата идёт с карты заказа. */
  supplement: (orderId: string, items: OrderItemInput[], cardToken?: string) =>
    request<SupplementResult>(`/v1/orders/${orderId}/supplement`, { method: 'POST', body: { items, ...(cardToken === undefined ? {} : { cardToken }) } }),
  cancel: (orderId: string, reason: string) =>
    request<CancelResult>(`/v1/orders/${orderId}/cancel`, { method: 'POST', body: { reason } }),
  tracking: (orderId: string) => request<OrderTracking>(`/v1/orders/${orderId}/tracking`),
  /** Оценка и чаевые: сервер списывает чаевые картой заказа, `declined` — карта отказала. */
  rate: (orderId: string, body: { stars: number; tipRsd?: Rsd | undefined; comment?: string | undefined }) =>
    request<RatingResult>(`/v1/orders/${orderId}/rating`, { method: 'POST', body }),
};

export const reports = {
  create: (body: { productId?: string; orderId?: string; reason: string; text?: string | undefined }) =>
    request<{ id: string }>('/v1/me/reports', { method: 'POST', body }),
};

export const support = {
  /** Переписка с поддержкой: открытие отмечает ответы прочитанными. */
  get: (orderId: string | null) => request<SupportView>('/v1/me/support', { query: { orderId } }),
  send: (text: string, orderId: string | null) =>
    request<SupportView>('/v1/me/support/messages', { method: 'POST', body: { text, ...(orderId ? { orderId } : {}) } }),
};

export const notifications = {
  list: (limit = 100) => request<NotificationsPage>('/v1/me/notifications', { query: { limit } }),
  /** Без списка идентификаторов прочитанным отмечается всё. */
  markAllRead: () => request<{ ok: boolean }>('/v1/me/notifications/read', { method: 'POST', body: {} }),
  settings: () => request<NotificationSettings>('/v1/me/notification-settings'),
  updateSettings: (patch: { orders?: boolean | undefined; promo?: boolean | undefined }) =>
    request<NotificationSettings>('/v1/me/notification-settings', { method: 'PATCH', body: patch }),
};

/** Push в браузере: открытый ключ отправителя и подписка этого браузера как устройство аккаунта. */
export const push = {
  /** `null` — на сервере push в браузер не настроен. */
  webKey: () => request<{ publicKey: string | null }>('/v1/push/web-key'),
  register: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    request<{ ok: boolean }>('/v1/me/devices', {
      method: 'PUT',
      body: { token: subscription.endpoint, platform: 'web', app: 'client', keys: subscription.keys },
    }),
  remove: (endpoint: string) => request<{ removed: boolean }>(`/v1/me/devices/${encodeURIComponent(endpoint)}`, { method: 'DELETE' }),
};

export const events = {
  /** Лента событий сессии; гостю приходят только остатки склада его точки и статус магазина. */
  listen: (
    scope: StorefrontScope,
    lastEventId: string | null,
    signal: AbortSignal,
    onFrame: (frame: StreamFrame) => void,
  ) => stream('/v1/events', { query: scopeQuery(scope), lastEventId, signal, onFrame }),
};

/** Страницы условий и политики несут все языки; `?lang=` открывает язык приложения. */
export const payments = {
  config: () => request<PaymentConfig>('/v1/payments/config'),
};

export const links = {
  terms: (lang: Lang) => `${WEB_BASE_URL}/terms?lang=${lang}`,
  privacy: (lang: Lang) => `${WEB_BASE_URL}/privacy?lang=${lang}`,
  productShare: (productId: string) => `${WEB_BASE_URL}/p/${productId}`,
};
